import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { LoginForm } from '../components/LoginForm'

const Auth = () => {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login'
  const [mode, setMode] = useState(initialMode) // 'login' or 'register'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    acceptTerms: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  const { login, register, error, clearError, resendConfirmation } = useAuth()
  const navigate = useNavigate()

  // Get return URL from location state
  const from = location.state?.from?.pathname || '/'

  // Clear error when switching modes
  useEffect(() => {
    clearError()
  }, [mode, clearError])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    // Clear error when user starts typing
    if (error) clearError()
  }

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      return 'Email and password are required'
    }

    if (mode === 'register') {
      if (!formData.name) {
        return 'Name is required'
      }
      if (formData.password !== formData.confirmPassword) {
        return 'Passwords do not match'
      }
      if (formData.password.length < 8) {
        return 'Password must be at least 8 characters'
      }
      if (!formData.acceptTerms) {
        return 'You must accept the terms and conditions'
      }
    }

    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    const validationError = validateForm()
    if (validationError) {
      return // Error will be shown from context
    }

    setIsLoading(true)

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password)
      } else {
        const result = await register({
          name: formData.name,
          email: formData.email,
          password: formData.password
        })
        
        // Check if email confirmation is required
        if (result.needsConfirmation) {
          setShowConfirmation(true)
          return
        }
      }
      
      // Redirect to return URL or home
      navigate(from, { replace: true })
    } catch (err) {
      // Error handled by context
      console.error('Auth error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login')
    setFormData({
      email: formData.email, // Keep email when switching
      password: '',
      confirmPassword: '',
      name: '',
      acceptTerms: false
    })
  }

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <a href="/" className="flex items-center gap-2 font-medium">
            <div className="bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-md">
              <span className="text-sm">🍕</span>
            </div>
            Kinora
          </a>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            {showConfirmation ? (
              <div className="text-center space-y-4">
                <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                  <p className="text-sm text-primary">
                    Please check your email and click the confirmation link to complete your registration.
                  </p>
                </div>
                <button
                  onClick={() => resendConfirmation(formData.email)}
                  className="text-sm text-primary hover:underline"
                >
                  Resend confirmation email
                </button>
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="block w-full text-sm text-muted-foreground hover:text-foreground"
                >
                  Back to login
                </button>
              </div>
            ) : (
              <LoginForm
                mode={mode}
                formData={formData}
                onInputChange={handleInputChange}
                onSubmit={handleSubmit}
                onToggleMode={toggleMode}
                isLoading={isLoading}
                error={error}
              />
            )}
          </div>
        </div>
      </div>
      <div className="bg-muted relative hidden lg:block">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-background/50" />
        <div className="absolute inset-0 flex items-center justify-center p-8">
          <div className="text-center space-y-6 max-w-md">
            <div className="space-y-2">
              <h2 className="text-3xl font-bold text-foreground">
              {showConfirmation ? 'Check Your Email' : 'Welcome to Kinora'}
</h2>
<p className="text-lg text-muted-foreground">
              {showConfirmation 
                ? 'We\'ve sent you a confirmation email. Please check your inbox and click the link to verify your account.'
                : 'Stream independent films, series, and documentaries — then decide which creators get paid with your monthly slices'
              }
            </p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold">1</span>
                </div>
                <h3 className="font-semibold">Stream Indie Content</h3>
                <p className="text-muted-foreground">Watch films, series, and documentaries from independent creators and studios</p>
              </div>
              <div className="space-y-2">
                <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold">2</span>
                </div>
                <h3 className="font-semibold">Get Monthly Slices</h3>
                <p className="text-muted-foreground">Your subscription comes with slices each month — your way of supporting what you watch</p>
              </div>
              <div className="space-y-2">
                <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold">3</span>
                </div>
                <h3 className="font-semibold">Support What You Love</h3>
                <p className="text-muted-foreground">Donate slices to the content that moves you — creators get paid based on your support</p>
              </div>
              <div className="space-y-2">
                <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold">4</span>
                </div>
                <h3 className="font-semibold">Fund What's Next</h3>
                <p className="text-muted-foreground">Back crowdfunding campaigns to help creators produce their next project</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Auth